/*
Copyright (c) 2012-2025 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { camelCase, pick } from "lodash"

import { TextType, TranslationType } from "constants/menu"
import {
  LanguageFullName,
  MenuTranslationRequestData,
  MenuTranslationSetting,
  TranslationSetting,
} from "types/models/menu"

const autoTranslationQueueKeyPrefix = (
  entityType: string,
  textType: string,
  targetLang: string,
) => `${camelCase(entityType)}:${camelCase(textType)}:${camelCase(targetLang)}:`

export const generateAutoTranslationQueueKeys = (
  data: MenuTranslationRequestData,
) => {
  const extraKey = Date.now().toString()
  const res = data.textTargetLang?.map(
    lang =>
      `${autoTranslationQueueKeyPrefix(data.entity, data.textType, lang)}${
        data.text
      }_${extraKey}`,
  )
  return res ?? []
}

export const isAutoTranslationQueueExists = ({
  queue,
  entityType,
  textType,
  targetLang,
}: {
  queue: Set<string> | undefined
  entityType: TranslationType
  textType?: TextType
  targetLang?: keyof LanguageFullName
}) => {
  if (!queue || !queue.size) {
    return false
  }

  let prefix = camelCase(entityType)
  if (textType && targetLang) {
    prefix = autoTranslationQueueKeyPrefix(entityType, textType, targetLang)
  }

  // eslint-disable-next-line no-restricted-syntax
  for (const value of queue) {
    if (value?.startsWith(prefix)) {
      return true
    }
  }

  return false
}

export const generateTranslationParam = ({
  menuTranslationSetting,
  translationSetting,
  originalTranslationFromDSMap,
  entity,
  textType,
  text,
  destinationKey,
}: {
  menuTranslationSetting?: MenuTranslationSetting
  translationSetting?: TranslationSetting
  originalTranslationFromDSMap: Record<
    string,
    TranslationSetting["originalTranslationFromDS"]
  >
  entity: MenuTranslationRequestData["entity"]
  textType: MenuTranslationRequestData["textType"]
  text: MenuTranslationRequestData["text"]
  destinationKey?: MenuTranslationRequestData["destinationKey"]
}): TranslationSetting | undefined => {
  if (!translationSetting) {
    return translationSetting
  }

  let originalTranslationFromDS: TranslationSetting["originalTranslationFromDS"]
  // description can be empty
  if (text || textType === "description") {
    const key = generateTranslationFromDSKey({
      entity,
      textType,
      text,
      destinationKey,
    })
    originalTranslationFromDS = originalTranslationFromDSMap[key]
  }

  return {
    translation:
      removeUnsupportedTranslations(
        menuTranslationSetting,
        translationSetting.translation,
      ) ?? {},
    originalTranslationFromDS: removeUnsupportedTranslations(
      menuTranslationSetting,
      originalTranslationFromDS,
    ),
    originalText: undefined,
  }
}

const removeUnsupportedTranslations = (
  menuTranslationSetting?: MenuTranslationSetting,
  translation?: TranslationSetting["translation"],
): TranslationSetting["translation"] | undefined => {
  if (!translation) {
    return translation
  }

  const filteredTranslation = pick(
    translation,
    menuTranslationSetting?.menuSupportedLanguages ?? [],
  )

  return filteredTranslation
}

export const nameTranslationHelper = {
  getTranslation: (objMap: TranslationSetting, localeKey: string) =>
    objMap.translation?.[localeKey],
  getFormKey: (localeKey?: string) =>
    `nameTranslation.translation.${localeKey}`,
}

export const descriptionTranslationHelper = {
  getTranslation: (objMap: TranslationSetting, localeKey: string) =>
    objMap.translation?.[localeKey],
  getFormKey: (localeKey?: string) =>
    `descriptionTranslation.translation.${localeKey}`,
}

export const translationMandatoryUnfill = (
  translationFieldMandatoryLanguages?: string[],
  nameTranslation?: TranslationSetting,
) => {
  const mandatoryLanguages = translationFieldMandatoryLanguages || []
  for (let i = 0; i < mandatoryLanguages.length; i += 1) {
    if (!nameTranslation?.translation[mandatoryLanguages[i]]) {
      return true
    }
  }
  return false
}

export const generateTranslationFromDSKey = ({
  entity,
  textType,
  text,
  destinationKey,
}: Pick<
  MenuTranslationRequestData,
  "entity" | "textType" | "text" | "destinationKey"
>) => {
  if (destinationKey) {
    return `${entity}:${textType}:${text}:${destinationKey}`
  }

  return `${entity}:${textType}:${text}`
}

export const getItemTranslationErrorKey = (
  textType: MenuTranslationRequestData["textType"],
  text: string,
) => {
  return `${textType}:${text}`
}
